import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Users, KanbanSquare, ShieldCheck, Settings, ScrollText, LogOut, Menu, X, BarChart3, MessageCircle, Megaphone, Mail } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { LanguageSwitcher } from "@/components/PublicLayout";

export default function AdminLayout({ children }) {
  const { t, lang } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const p = (path) => `/${lang}/admin${path}`;
  const canManageUsers = ["super_admin", "admin"].includes(user?.role);
  const canAudit = ["super_admin", "admin"].includes(user?.role);
  const canSettings = ["super_admin", "admin", "manager"].includes(user?.role);

  const items = [
    { to: p(""), key: "dashboard", label: t("admin.dashboard"), Icon: LayoutDashboard, end: true },
    { to: p("/candidates"), key: "candidates", label: t("admin.candidates"), Icon: Users },
    { to: p("/pipeline"), key: "pipeline", label: t("admin.pipeline"), Icon: KanbanSquare },
    { to: p("/stats"), key: "stats", label: t("stats.title"), Icon: BarChart3 },
    { to: p("/recruitment"), key: "recruitment", label: t("admin.recruitment"), Icon: Megaphone },
    canManageUsers && { to: p("/users"), key: "users", label: t("admin.users"), Icon: ShieldCheck },
    canAudit && { to: p("/audit"), key: "audit", label: t("admin.audit"), Icon: ScrollText },
    canAudit && { to: p("/recruit-ops"), key: "recruit-ops", label: "Recruitment Ops", Icon: Megaphone },
    canAudit && { to: p("/livechat"), key: "livechat", label: t("admin.livechat"), Icon: MessageCircle },
    canAudit && { to: p("/contact"), key: "contact", label: t("admin.contact"), Icon: Mail },
    canSettings && { to: p("/settings"), key: "settings", label: t("admin.settings"), Icon: Settings },
  ].filter(Boolean);

  const doLogout = async () => {
    await logout();
    navigate(`/${lang}`);
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-surface">
        <div className="flex h-14 items-center justify-between gap-4 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button type="button" data-testid="admin-menu-toggle" className="lg:hidden" onClick={() => setOpen((v) => !v)} aria-label={t("common.menu")}>
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
            <Link to={`/${lang}`} className="font-display text-base font-semibold tracking-tight">
              CONSULTANTS SPARKS
            </Link>
            <span className="hidden text-xs uppercase tracking-[0.2em] text-muted-foreground sm:inline">
              {t("cta.admin")}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher testId="admin-language-switcher" />
            <span data-testid="admin-user-role" className="hidden text-xs text-muted-foreground sm:inline">
              {user?.email} · {user?.role}
            </span>
            <button data-testid="admin-logout" onClick={doLogout} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline">{t("cta.logout")}</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex overflow-x-hidden">
        <aside
          data-testid="admin-sidebar"
          className={`${open ? "block" : "hidden"} fixed inset-y-14 left-0 z-30 w-60 border-r border-border bg-surface p-3 lg:sticky lg:top-14 lg:block lg:h-[calc(100vh-3.5rem)]`}
        >
          <nav className="flex flex-col gap-1">
            {items.map(({ to, key, label, Icon, end }) => (
              <NavLink
                key={key}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                data-testid={`admin-nav-${key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 text-sm transition-colors duration-200 ${
                    isActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </nav>
        </aside>

        <main className="min-w-0 flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
